/** "My apps" — the list, the name/client-ID filter, and the create dialog. */

import {
  ApiError,
  createApplication,
  getApplications,
  suggestApplication,
  type DeveloperApp,
} from '../api.ts';
import { route } from '../config.ts';
import { clear, el, errorState, field, openModal, toast } from '../ui.ts';

const PREFILL_KEY = 'lp-console-app';

/** Handed to the detail page so it can paint before its own fetch lands. */
export function prefillApp(app: DeveloperApp): void {
  try {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify(app));
  } catch {
    /* the detail page just fetches instead */
  }
}

export function readPrefill(id: string): DeveloperApp | null {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DeveloperApp;
    return parsed?.id === id ? parsed : null;
  } catch {
    return null;
  }
}

const detailUrl = (app: DeveloperApp) => `${route('apps/manage')}?id=${encodeURIComponent(app.id)}`;

function appCard(app: DeveloperApp): HTMLElement {
  const avatar = app.avatar
    ? el('img', { class: 'app-avatar', src: app.avatar, alt: '', loading: 'lazy', decoding: 'async' })
    : el('div', { class: 'app-avatar is-placeholder', 'aria-hidden': 'true' }, [
        el('span', { text: (app.name || '?').trim().charAt(0).toUpperCase() }),
      ]);

  const manage = el('a', { class: 'btn btn-ghost', href: detailUrl(app), text: 'Manage' });
  manage.addEventListener('click', () => prefillApp(app));

  return el('article', { class: 'app-card' }, [
    avatar,
    el('div', { class: 'app-card-main' }, [
      el('h2', { class: 'app-card-name', text: app.name || 'Untitled app' }),
      el('p', { class: 'app-card-id' }, [
        el('span', { class: 'app-card-id-label', text: 'Client ID' }),
        el('code', { text: app.clientID ?? '—' }),
      ]),
    ]),
    el('div', { class: 'app-card-actions' }, [manage]),
  ]);
}

function emptyState(onCreate: () => void): HTMLElement {
  const button = el('button', { class: 'btn btn-primary', type: 'button', text: 'Create your first app' });
  button.addEventListener('click', onCreate);

  return el('div', { class: 'console-empty' }, [
    el('p', { class: 'console-empty-title', text: 'No apps yet' }),
    el('p', {
      class: 'console-empty-note',
      text: 'An app gives you a client ID and secret for authenticating against the Livepush API.',
    }),
    button,
  ]);
}

function noMatches(query: string): HTMLElement {
  return el('div', { class: 'console-empty' }, [
    el('p', { class: 'console-empty-title', text: 'No matching apps' }),
    el('p', { class: 'console-empty-note', text: `Nothing matches “${query}”.` }),
  ]);
}

export async function mountApps(container: HTMLElement): Promise<void> {
  const filterInput = document.querySelector<HTMLInputElement>('[data-app-filter]');
  const newButton = document.querySelector<HTMLButtonElement>('[data-new-app]');

  let apps: DeveloperApp[] = [];
  let suggestedName = '';

  const render = () => {
    const query = (filterInput?.value ?? '').trim().toLowerCase();
    const visible = query
      ? apps.filter(
          (app) =>
            app.name?.toLowerCase().includes(query) || app.clientID?.toLowerCase().includes(query),
        )
      : apps;

    clear(container);

    if (!apps.length) {
      container.append(emptyState(openCreate));
      return;
    }
    if (!visible.length) {
      container.append(noMatches(query));
      return;
    }

    const grid = el('div', { class: 'app-grid' }, visible.map(appCard));
    container.append(grid);
    container.append(
      el('p', {
        class: 'console-count',
        text:
          visible.length === apps.length
            ? `${apps.length} app${apps.length === 1 ? '' : 's'}`
            : `${visible.length} of ${apps.length} apps`,
      }),
    );
  };

  function openCreate(): void {
    const name = field({
      label: 'App name',
      id: 'new-app-name',
      value: suggestedName,
      placeholder: 'My integration',
      hint: 'Shown on the OAuth consent screen. You can change it later.',
    });
    const error = el('p', { class: 'field-error', hidden: true });

    openModal({
      title: 'Create a developer app',
      body: el('div', { class: 'field-stack' }, [
        name.wrap,
        el('p', {
          class: 'field-hint',
          text: 'You can upload an avatar once the app exists.',
        }),
        error,
      ]),
      confirmLabel: 'Create app',
      onConfirm: async () => {
        const value = name.input.value.trim();
        if (!value) {
          error.textContent = 'Give the app a name.';
          error.hidden = false;
          name.input.focus();
          return false;
        }

        try {
          const created = await createApplication({ name: value });
          apps = [...apps, created];
          render();
          toast(`“${created.name}” created.`);
          void loadSuggestion();
          return true;
        } catch (cause) {
          error.textContent =
            cause instanceof ApiError ? cause.message : 'Could not create the app. Try again.';
          error.hidden = false;
          return false;
        }
      },
    });
  }

  async function loadSuggestion(): Promise<void> {
    try {
      const suggestion = await suggestApplication();
      suggestedName = suggestion?.name ?? '';
    } catch {
      suggestedName = '';
    }
  }

  filterInput?.addEventListener('input', render);
  newButton?.addEventListener('click', openCreate);

  try {
    apps = (await getApplications()) ?? [];
  } catch (cause) {
    clear(container);
    container.append(
      errorState(
        cause instanceof ApiError ? cause.message : 'Could not load your apps.',
        () => window.location.reload(),
      ),
    );
    return;
  }

  if (filterInput) filterInput.disabled = false;
  if (newButton) newButton.disabled = false;
  render();
  void loadSuggestion();
}
